import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  Package,
  User,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  Upload,
  FileText,
  Eye,
  Plus,
  Trash2,
  Settings,
  ChevronDown,
  ChevronRight,
  Building2,
  Pencil,
  SlidersHorizontal,
} from 'lucide-react';
import { API, BACKEND_URL_EXPORT as BACKEND_URL } from '../../config';
import { extractErrorMessage } from '../../utils/errorHandler';

const fmtPKR = (v) =>
  `PKR ${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtJPY = (v) =>
  `¥${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
const fmtUSD = (v) =>
  `$${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const levelInfo = (lvl) => {
  if (lvl === 'manager') {
    return {
      label: 'Manager',
      cls: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    };
  }
  if (lvl === 'partner') {
    return {
      label: 'Partner',
      cls: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    };
  }
  return {
    label: 'Agent',
    cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  };
};

const emptyPayment = {
  employee_id: '',
  payment_period: '',
  total_commission_usd: 0,
  total_commission_jpy: 0,
  exchange_rate: 150,
  total_amount_pkr: '',
  payment_method: 'bank_transfer',
  transaction_ref: '',
  notes: '',
};

const settingsFields = [
  { key: 'exporter_rate', label: 'Exporter Cut Rate' },
  { key: 'manager_rate', label: 'Manager Rate' },
  { key: 'srb_rate', label: 'SRB (Sindh Revenue) Rate' },
  { key: 'federal_rate', label: 'Federal Sales Tax Rate' },
  { key: 'fbr_rate', label: 'FBR Rate' },
  { key: 'co_cut_rate', label: 'Company Cut Rate' },
  { key: 'partner_rate', label: 'Partner Rate' },
  { key: 'yen_to_pkr', label: 'YEN to PKR Rate' },
];

// Per-agent overrides reuse the same eight global keys PLUS a per-agent-only
// compensation amount. A blank field means "use the global CommissionSettings
// value" (shown as the placeholder); for compensation, blank/0 means none.
const overrideFields = [
  ...settingsFields,
  { key: 'compensation_rate', label: 'Compensation (PKR)' },
];

// The admin picks what the per-employee compensation_rate multiplies.
// "vehicle" => delivered vehicles (default), "order" => delivered orders.
const compensationBasisOptions = [
  { value: 'vehicle', label: 'Per delivered vehicle' },
  { value: 'order', label: 'Per delivered order' },
];

// `delivered_count` is the number of delivered ORDERS (drives unit/base pay).
// `delivered_vehicle_count` is the vehicle total used for the per-vehicle
// compensation deduction. Fall back to the order count when the backend has not
// yet been restarted with the newer field.
const deliveredVehicles = (emp) =>
  emp.delivered_vehicle_count ?? emp.delivered_count ?? 0;

// Basis actually in effect for an employee. Fall back to "vehicle" so a stale
// backend response never silently switches the deduction to per-order.
const compensationBasisOf = (emp) =>
  emp.compensation_basis === 'order' ? 'order' : 'vehicle';

// Unit count the compensation was multiplied by.
const compensationUnits = (emp) =>
  emp.compensation_units ??
  (compensationBasisOf(emp) === 'order'
    ? emp.delivered_count ?? 0
    : deliveredVehicles(emp));

const EmployeeCommission = () => {
  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [commissionPayments, setCommissionPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({ ...emptyPayment, payment_period: defaultMonth });
  const [editingPayment, setEditingPayment] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(null);

  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);

  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideEmployee, setOverrideEmployee] = useState(null);
  const [overrideForm, setOverrideForm] = useState({});
  const [savingOverride, setSavingOverride] = useState(false);

  const fetchData = async (period) => {
    setLoading(true);
    setError(null);
    try {
      const [monthlyRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/employee/commissions/monthly?month=${period}`, {
          headers: authHeaders,
        }),
        axios.get(`${API}/commission-payments`, { headers: authHeaders }),
      ]);
      setData(monthlyRes.data);
      setCommissionPayments(paymentsRes.data || []);
      if (monthlyRes.data?.settings) {
        const s = monthlyRes.data.settings;
        const form = {};
        settingsFields.forEach(({ key }) => {
          form[key] = s[key] != null ? String(s[key]) : '';
        });
        setSettingsForm(form);
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(month);
  }, [month]);

  const isAdmin = !!data?.is_admin;
  const employees = data?.employees || [];
  const company = data?.company;
  const totalDelivered = employees.reduce(
    (sum, e) => sum + deliveredVehicles(e),
    0
  );

  const paidForEmployee = (empId) => {
    const period = (month || '').slice(0, 7);
    return commissionPayments
      .filter(
        (p) =>
          p.employee_id === empId &&
          (p.payment_period || '').slice(0, 7) === period
      )
      .reduce(
        (sum, p) =>
          sum +
          (p.total_amount_pkr != null
            ? Number(p.total_amount_pkr)
            : Number(p.total_commission_jpy || 0) * Number(p.exchange_rate || 150)),
        0
      );
  };

  const getReceiptUrl = (p) => {
    if (!p?.receipt_file_path) return null;
    const m = String(p.receipt_file_path).match(/receipts\/([\w.\-\s]+)/);
    return m ? `${API}/commission-receipts/${m[1]}` : null;
  };

  const handleViewReceipt = async (p) => {
    const url = getReceiptUrl(p);
    if (!url) return;
    try {
      const response = await fetch(url, { headers: authHeaders });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const win = window.open(blobUrl, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
      if (!win) toast.warning('Please allow popups to view receipts');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleReceiptUpload = async (paymentId, file) => {
    if (!file) return;
    setUploadingReceipt(paymentId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/commission-payments/${paymentId}/receipt`, formData, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Receipt uploaded');
      fetchData(month);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setUploadingReceipt(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentFormData((prev) => ({ ...prev, [name]: value }));
  };

  const openCreatePayment = () => {
    setEditingPayment(null);
    setPaymentFormData({
      ...emptyPayment,
      payment_period: month,
      employee_id: employees.length === 1 ? employees[0].employee_id : '',
    });
    setShowPaymentDialog(true);
  };

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setPaymentFormData({
      employee_id: p.employee_id || '',
      payment_period: p.payment_period || month,
      total_commission_usd: p.total_commission_usd ?? 0,
      total_commission_jpy: p.total_commission_jpy ?? 0,
      exchange_rate: p.exchange_rate ?? 150,
      total_amount_pkr: p.total_amount_pkr != null ? String(p.total_amount_pkr) : '',
      payment_method: p.payment_method || '',
      transaction_ref: p.transaction_ref || '',
      notes: p.notes || '',
    });
    setShowPaymentDialog(true);
  };

  const handleSavePayment = async () => {
    if (!paymentFormData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!paymentFormData.payment_period) {
      toast.error('Payment period is required');
      return;
    }
    setSavingPayment(true);
    try {
      const payload = {
        employee_id: paymentFormData.employee_id,
        payment_period: paymentFormData.payment_period,
        total_commission_usd: parseFloat(paymentFormData.total_commission_usd || 0),
        total_commission_jpy: parseFloat(paymentFormData.total_commission_jpy || 0),
        exchange_rate: parseFloat(paymentFormData.exchange_rate || 150),
        total_amount_pkr:
          paymentFormData.total_amount_pkr === ''
            ? null
            : parseFloat(paymentFormData.total_amount_pkr),
        payment_method: paymentFormData.payment_method || null,
        transaction_ref: paymentFormData.transaction_ref || null,
        notes: paymentFormData.notes || null,
      };
      if (editingPayment) {
        await axios.put(`${API}/commission-payments/${editingPayment.id}`, payload, {
          headers: authHeaders,
        });
        toast.success('Payment updated');
      } else {
        await axios.post(`${API}/commission-payments`, payload, {
          headers: authHeaders,
        });
        toast.success('Payment recorded');
      }
      setShowPaymentDialog(false);
      setEditingPayment(null);
      fetchData(month);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingPayment(false);
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this commission payment?')) return;
    try {
      await axios.delete(`${API}/commission-payments/${paymentId}`, {
        headers: authHeaders,
      });
      toast.success('Payment deleted');
      fetchData(month);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleSaveSettings = async () => {
    const payload = {};
    settingsFields.forEach(({ key }) => {
      const num = parseFloat(settingsForm[key]);
      if (!isNaN(num)) payload[key] = num;
    });
    if (Object.keys(payload).length === 0) {
      toast.error('No valid settings to save');
      return;
    }
    setSavingSettings(true);
    try {
      await axios.put(`${API}/commission-settings`, payload, {
        headers: authHeaders,
      });
      toast.success('Commission settings saved');
      setShowSettingsDialog(false);
      fetchData(month);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleEmployee = (empId) => {
    setExpandedEmployeeId((prev) => (prev === empId ? null : empId));
  };

  const openOverrideDialog = (emp) => {
    const stored = emp.rate_overrides || {};
    const form = {};
    overrideFields.forEach(({ key }) => {
      form[key] = stored[key] != null ? String(stored[key]) : '';
    });
    form.compensation_basis = compensationBasisOf(emp);
    setOverrideForm(form);
    setOverrideEmployee(emp);
    setShowOverrideDialog(true);
  };

  // `compensation_basis` is a string selector stored in the same map as the
  // numeric rates, so counting raw keys would read "9 of 8 fields". Count only
  // the keys that are real rate fields.
  const overrideRateKeys = (emp) => {
    const stored = emp?.rate_overrides || {};
    return overrideFields.map(({ key }) => key).filter((key) => stored[key] != null);
  };

  // Based on the rate keys, not raw keys: a basis-only record (accepted by the
  // API even though the UI never writes one) customizes no rate, so it must not
  // show a "Custom rates" badge or "custom rates for 0 of 8 fields".
  const hasOverride = (emp) => overrideRateKeys(emp).length > 0;

  // Only non-blank fields are sent; if every field is blank we send {}
  // which the backend treats as "clear all overrides". The basis is a string
  // selector, so it is sent whenever a compensation rate is present.
  const buildOverridePayload = () => {
    const payload = {};
    overrideFields.forEach(({ key }) => {
      const num = parseFloat(overrideForm[key]);
      if (!isNaN(num)) payload[key] = num;
    });
    if (payload.compensation_rate != null) {
      payload.compensation_basis =
        overrideForm.compensation_basis === 'order' ? 'order' : 'vehicle';
    }
    return payload;
  };

  const handleSaveOverride = async () => {
    if (!overrideEmployee?.employee_id) return;
    const payload = buildOverridePayload();
    setSavingOverride(true);
    try {
      await axios.put(
        `${API}/users/${overrideEmployee.employee_id}`,
        { commission_rate_overrides: payload },
        { headers: authHeaders }
      );
      toast.success(
        Object.keys(payload).length === 0
          ? `Overrides cleared for ${overrideEmployee.name || 'agent'} - using global rates`
          : `Rates saved for ${overrideEmployee.name || 'agent'}`
      );
      setShowOverrideDialog(false);
      setOverrideEmployee(null);
      fetchData(month);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingOverride(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date)) return '—';
    return date.toLocaleDateString('en-US');
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Loading commissions...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-red-500">
        <AlertCircle className="mr-2 h-4 w-4" />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">
            {isAdmin ? 'Employee Commission Summary' : 'My Commission'}
          </h2>
          <p className="text-sm text-muted-foreground">
            Monthly settlements derived from delivered vehicle orders.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-40"
            />
          </div>
          {isAdmin && (
            <Button onClick={openCreatePayment}>
              <Plus className="mr-2 h-4 w-4" /> Record Payment
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
              <Settings className="mr-2 h-4 w-4" /> Settings
            </Button>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Grand Total (PKR)</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {fmtPKR(data?.grand_total_pkr ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Delivered Vehicles</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDelivered}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Employees</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{employees.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Company block (admin only) */}
      {isAdmin && company && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Building2 className="h-4 w-4" />
              Company Cuts for {month}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <div className="text-xs text-muted-foreground">Total Sales (JPY)</div>
              <div className="font-semibold">{fmtJPY(company.sell_jpy_total ?? 0)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Total Sales (PKR)</div>
              <div className="font-semibold">{fmtPKR(company.sell_pkr_total ?? 0)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">FBR Cut</div>
              <div className="font-semibold text-red-600">
                {fmtPKR(company.fbr_cut_pkr ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Company Cut</div>
              <div className="font-semibold text-orange-600">
                {fmtPKR(company.co_cut_pkr ?? 0)}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Exporter Cut</div>
              <div className="font-semibold text-blue-600">
                {fmtPKR(company.exporter_cut_pkr ?? 0)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Employee settlement cards */}
      <div className="space-y-4">
        {employees.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              No delivered orders or employees found for {month}.
            </CardContent>
          </Card>
        )}

        {employees.map((emp) => {
          const lvl = levelInfo(emp.commission_level);
          const paid = paidForEmployee(emp.employee_id);
          const due = Math.max(0, (emp.total_pkr || 0) - paid);
          const expanded = expandedEmployeeId === emp.employee_id;
          return (
            <Card key={emp.employee_id} className="overflow-hidden">
              <CardContent className="p-0">
                <button
                  type="button"
                  onClick={() => toggleEmployee(emp.employee_id)}
                  className="flex w-full flex-wrap items-center justify-between gap-4 p-5 text-left hover:bg-muted/40"
                >
                  <div className="flex items-center gap-3">
                    {expanded ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{emp.name || 'Unnamed'}</span>
                        <Badge variant="outline" className={lvl.cls}>
                          {lvl.label}
                        </Badge>
                        {isAdmin && hasOverride(emp) && (
                          <Badge
                            variant="secondary"
                            className="bg-amber-500/10 text-amber-700 border-amber-500/30"
                          >
                            Custom rates
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {deliveredVehicles(emp)} delivered vehicle
                        {deliveredVehicles(emp) === 1 ? '' : 's'}
                        {emp.delivered_count > 1 && (
                          <span>
                            {' '}
                            ({emp.delivered_count} order
                            {emp.delivered_count === 1 ? '' : 's'})
                          </span>
                        )}
                        {emp.base_salary_pkr != null && emp.base_salary_pkr > 0
                          ? ` · Base ${fmtPKR(emp.base_salary_pkr)}`
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Settlement</div>
                      <div className="text-lg font-bold">{fmtPKR(emp.total_pkr ?? 0)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Paid</div>
                      <div className="text-sm font-semibold text-emerald-600">
                        {fmtPKR(paid)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Due</div>
                      <div
                        className={`text-sm font-semibold ${
                          due > 0 ? 'text-amber-600' : 'text-muted-foreground'
                        }`}
                      >
                        {fmtPKR(due)}
                      </div>
                    </div>
                  </div>
                </button>

                {expanded && (
                  <div className="border-t">
                    {isAdmin && (
                      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-muted/20 px-5 py-3">
                        <div className="text-xs text-muted-foreground">
                          {hasOverride(emp)
                            ? `Using custom rates for ${overrideRateKeys(emp).length} of ${overrideFields.length} fields; blank fields use global settings.`
                            : 'Using global commission rates for all fields.'}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openOverrideDialog(emp)}
                        >
                          <SlidersHorizontal className="mr-2 h-4 w-4" />
                          Override Rates
                        </Button>
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Stock No</TableHead>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Delivered</TableHead>
                          <TableHead className="text-right">Sell (JPY)</TableHead>
                          <TableHead className="text-right">Sell (USD)</TableHead>
                          <TableHead className="text-right">Cost (JPY)</TableHead>
                          <TableHead className="text-right">Profit (JPY)</TableHead>
                          <TableHead className="text-right">Settlement (PKR)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(emp.orders || []).length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={8}
                              className="text-center text-muted-foreground"
                            >
                              No delivered orders in {month}.
                            </TableCell>
                          </TableRow>
                        )}
                        {(emp.orders || []).map((order) => (
                          <TableRow key={order.order_id}>
                            <TableCell className="font-medium">
                              {order.stock_no || '—'}
                              {order.vehicle_count > 1 && (
                                <Badge variant="secondary" className="ml-2">
                                  ×{order.vehicle_count} vehicles
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {order.vehicle_name || `#${String(order.order_id).slice(0, 8)}`}
                            </TableCell>
                            <TableCell>{formatDate(order.delivered_date)}</TableCell>
                            <TableCell className="text-right">
                              {fmtJPY(order.sell_price_jpy)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtUSD(order.sell_price_usd)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtJPY(order.total_cost_jpy)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtJPY(order.profit_jpy)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {fmtPKR(order.settlement_pkr)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex flex-wrap items-center justify-end gap-x-8 gap-y-3 border-t px-5 py-4">
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">
                          Gross settlement
                        </div>
                        <div className="text-sm font-semibold">
                          {fmtPKR(emp.gross_settlement_pkr ?? emp.total_pkr ?? 0)}
                        </div>
                      </div>
                      {(Number(emp.compensation_rate) > 0 ||
                        Number(emp.compensation_pkr) > 0) && (
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">
                            Less compensation ({fmtPKR(emp.compensation_rate)} ×{' '}
                            {compensationUnits(emp)} delivered{' '}
                            {compensationBasisOf(emp) === 'order' ? 'order' : 'vehicle'}
                            {compensationUnits(emp) === 1 ? '' : 's'})
                          </div>
                          <div className="text-sm font-semibold text-red-600">
                            -{fmtPKR(emp.compensation_pkr)}
                          </div>
                        </div>
                      )}
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">
                          Net settlement
                        </div>
                        <div className="text-base font-bold">
                          {fmtPKR(emp.total_pkr ?? 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Payments ledger */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Payments
            <span className="text-sm font-normal text-muted-foreground">for {month}</span>
          </CardTitle>
          {isAdmin && (
            <Button size="sm" onClick={openCreatePayment}>
              <Plus className="mr-2 h-4 w-4" /> New Payment
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {commissionPayments.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No payments recorded for this month.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead>Employee</TableHead>}
                  <TableHead>Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Amount (PKR)</TableHead>
                  <TableHead className="text-right">Amount (JPY)</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead className="text-right">Receipt</TableHead>
                  {isAdmin && <TableHead className="w-24"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {commissionPayments.map((p) => (
                  <TableRow key={p.id}>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{p.employee_name || '—'}</span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell>{formatDate(p.payment_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{p.payment_period || '—'}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {p.total_amount_pkr != null
                        ? fmtPKR(p.total_amount_pkr)
                        : fmtPKR(
                            Number(p.total_commission_jpy || 0) *
                              Number(p.exchange_rate || 150)
                          )}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {fmtJPY(p.total_commission_jpy)}
                    </TableCell>
                    <TableCell>
                      {p.payment_method
                        ? String(p.payment_method).replace(/_/g, ' ')
                        : '—'}
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate">
                      {p.transaction_ref || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isAdmin && (
                          <>
                            <input
                              type="file"
                              id={`receipt-${p.id}`}
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleReceiptUpload(p.id, f);
                                e.target.value = '';
                              }}
                            />
                            {getReceiptUrl(p) ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewReceipt(p)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  document.getElementById(`receipt-${p.id}`)?.click()
                                }
                                disabled={uploadingReceipt === p.id}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                            {getReceiptUrl(p) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  document.getElementById(`receipt-${p.id}`)?.click()
                                }
                                disabled={uploadingReceipt === p.id}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                        {!isAdmin && getReceiptUrl(p) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewReceipt(p)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {!isAdmin && !getReceiptUrl(p) && (
                          <FileText className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditPayment(p)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeletePayment(p.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Record / Edit Payment dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingPayment ? 'Edit Payment' : 'Record Payment'}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label>Employee</Label>
              <Select
                value={paymentFormData.employee_id}
                onValueChange={(v) =>
                  setPaymentFormData((prev) => ({ ...prev, employee_id: v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {(employees || []).map((emp) => (
                    <SelectItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.name || emp.employee_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Payment Period</Label>
                <Input
                  type="month"
                  name="payment_period"
                  value={paymentFormData.payment_period}
                  onChange={handleInputChange}
                />
              </div>
              <div className="grid gap-2">
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  defaultValue={new Date().toISOString().slice(0, 10)}
                  disabled
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="grid gap-2">
                <Label>Amount (PKR)</Label>
                <Input
                  type="number"
                  name="total_amount_pkr"
                  placeholder="0.00"
                  value={paymentFormData.total_amount_pkr}
                  onChange={handleInputChange}
                />
              </div>
              <div className="grid gap-2">
                <Label>Amount (USD)</Label>
                <Input
                  type="number"
                  name="total_commission_usd"
                  placeholder="0.00"
                  value={paymentFormData.total_commission_usd}
                  onChange={handleInputChange}
                />
              </div>
              <div className="grid gap-2">
                <Label>Amount (JPY)</Label>
                <Input
                  type="number"
                  name="total_commission_jpy"
                  placeholder="0.00"
                  value={paymentFormData.total_commission_jpy}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Exchange Rate</Label>
                <Input
                  type="number"
                  name="exchange_rate"
                  value={paymentFormData.exchange_rate}
                  onChange={handleInputChange}
                />
              </div>
              <div className="grid gap-2">
                <Label>Method</Label>
                <Select
                  value={paymentFormData.payment_method}
                  onValueChange={(v) =>
                    setPaymentFormData((prev) => ({ ...prev, payment_method: v }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="jazzcash">JazzCash</SelectItem>
                    <SelectItem value="easypaisa">Easypaisa</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Transaction Reference</Label>
              <Input
                name="transaction_ref"
                placeholder="e.g. bank slip / transaction id"
                value={paymentFormData.transaction_ref}
                onChange={handleInputChange}
              />
            </div>
            <div className="grid gap-2">
              <Label>Notes</Label>
              <Input
                name="notes"
                placeholder="Optional notes"
                value={paymentFormData.notes}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowPaymentDialog(false);
                setEditingPayment(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePayment} disabled={savingPayment}>
              {savingPayment
                ? 'Saving...'
                : editingPayment
                  ? 'Save Changes'
                  : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings dialog (admin only) */}
      {isAdmin && (
        <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Commission Settings</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              {settingsFields.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-2 items-center gap-3">
                  <Label className="text-sm">{label}</Label>
                  <Input
                    type="number"
                    step="any"
                    value={settingsForm[key] ?? ''}
                    onChange={(e) =>
                      setSettingsForm((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
              {data?.settings?.updated_at && (
                <p className="text-xs text-muted-foreground">
                  Last updated {new Date(data.settings.updated_at).toLocaleString()}
                  {data.settings.updated_by ? ` by #${String(data.settings.updated_by).slice(-8).toUpperCase()}` : ''}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowSettingsDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveSettings} disabled={savingSettings}>
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Per-agent rate override dialog (admin only) */}
      {isAdmin && overrideEmployee && (
        <Dialog
          open={showOverrideDialog}
          onOpenChange={(open) => {
            setShowOverrideDialog(open);
            if (!open) setOverrideEmployee(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Override Rates - {overrideEmployee.name || 'Agent'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              <p className="text-xs text-muted-foreground">
                Leave a field blank to use the global commission setting. These
                overrides apply to this agent only, and also drive the company
                FBR / Company Cut / Exporter Cut totals for their sales.
              </p>
              <p className="text-xs text-muted-foreground">
                Compensation is a PKR amount deducted for this employee only,
                shown as its own line and already included in the settlement
                total. Choose whether it applies per delivered vehicle or per
                delivered order. Leave the amount blank or 0 for no
                compensation.
              </p>
              {overrideFields.map(({ key, label }) => (
                <div key={key} className="grid grid-cols-2 items-center gap-3">
                  <Label className="text-sm">{label}</Label>
                  <Input
                    type="number"
                    step="any"
                    placeholder={
                      data?.settings?.[key] != null ? String(data.settings[key]) : ''
                    }
                    value={overrideForm[key] ?? ''}
                    onChange={(e) =>
                      setOverrideForm((prev) => ({
                        ...prev,
                        [key]: e.target.value,
                      }))
                    }
                  />
                </div>
              ))}
              <div className="grid grid-cols-2 items-center gap-3">
                <Label className="text-sm">Compensation basis</Label>
                <Select
                  value={
                    overrideForm.compensation_basis === 'order' ? 'order' : 'vehicle'
                  }
                  onValueChange={(value) =>
                    setOverrideForm((prev) => ({
                      ...prev,
                      compensation_basis: value,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select basis" />
                  </SelectTrigger>
                  <SelectContent>
                    {compensationBasisOptions.map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  const cleared = {};
                  overrideFields.forEach(({ key }) => {
                    cleared[key] = '';
                  });
                  cleared.compensation_basis = 'vehicle';
                  setOverrideForm(cleared);
                }}
                disabled={savingOverride}
              >
                Clear All
              </Button>
              <Button variant="outline" onClick={() => setShowOverrideDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveOverride} disabled={savingOverride}>
                {savingOverride ? 'Saving...' : 'Save Rates'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default EmployeeCommission;